/**
 * Pro identity layer — the single source for "who is the logged-in agent".
 *
 * The professional profile (`/api/professional/profile`) carries the agent's
 * brokerage/affiliation, license, and founding status, but NOT their person
 * name — that lives on the Clerk user (synced into the `users` table). So the
 * displayed identity is composed from two sources:
 *
 *   - NAME / EMAIL / AVATAR / HEADSHOT ← Clerk (read off the `window.Clerk` global, the
 *     same provider-agnostic pattern as `useProToken`, so it works identically
 *     in preview — no <ClerkProvider> — and in prod without a conditional hook).
 *   - ORG / ROLE            ← the professional profile (ProProfileVM).
 *
 * `composeProUser` is pure so the mapping is unit-tested without React; the
 * components (sidebar, account card, profile header) all render the same shape.
 */

import { IS_PREVIEW } from "./pro-mode";
import type { ProProfileVM, ProUserResult } from "./view-model";

/** Identity read off Clerk. `avatar` is precomputed initials. */
export interface ClerkIdentity {
  name: string;
  email?: string;
  avatar: string;
}

/** Editable first/last name parts read off (and written back to) Clerk. */
export interface ClerkNameParts {
  firstName: string;
  lastName: string;
}

/** The slice of the Clerk user resource we read off the global. */
interface ClerkUserGlobal {
  user?: {
    /** Stable Clerk user id (e.g. "user_2ab…"); the cross-surface PostHog distinct_id. */
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    fullName?: string | null;
    primaryEmailAddress?: { emailAddress?: string | null } | null;
    /** Clerk's write path; absent in preview (no user resource). */
    update?: (params: { firstName?: string; lastName?: string }) => Promise<unknown>;
    /**
     * Clerk always resolves `imageUrl` (to a generated default avatar when no
     * upload exists), so `hasImage` — never URL presence — is the signal.
     */
    imageUrl?: string | null;
    hasImage?: boolean;
    /** `{ file: null }` removes the image. Absent in preview. */
    setProfileImage?: (params: { file: Blob | File | null }) => Promise<unknown>;
    reload?: () => Promise<unknown>;
  } | null;
  /** Clerk's state listener; fires on any session/user change. */
  addListener?: (cb: () => void) => () => void;
}

/** The signed-in Clerk user resource, or null (preview / not yet hydrated). */
function clerkUser(): NonNullable<ClerkUserGlobal["user"]> | null {
  return (globalThis as { Clerk?: ClerkUserGlobal }).Clerk?.user ?? null;
}

/**
 * The signed-in agent's stable Clerk user id, or "" when Clerk isn't mounted /
 * the user resource hasn't hydrated (preview, or a render before sign-in). Read
 * off the `window.Clerk` global — the same provider-agnostic pattern as the
 * rest of this module — so it works without a <ClerkProvider> (preview) and in
 * prod. This is the PostHog `distinct_id` the Pro app identifies with, matching
 * the consumer app's `posthog.identify(clerkUserId)`.
 */
export function readClerkUserId(): string {
  return clerkUser()?.id?.trim() ?? "";
}

/** Initials from a display name (max 2), e.g. "Dana Rivera" → "DR". */
export function proInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase();
}

/**
 * Read the signed-in agent's identity off the Clerk global. Returns an empty
 * identity (never throws) when Clerk isn't mounted or no user is present —
 * preview has no provider, and a hard refresh may render before the user
 * resource hydrates (the auth gate guarantees it for the authed shell).
 */
export function readClerkIdentity(): ClerkIdentity {
  const u = clerkUser();
  if (!u) return { name: "", avatar: "" };
  const name =
    u.fullName?.trim() || `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
  const email = u.primaryEmailAddress?.emailAddress?.trim() || undefined;
  return { name, email, avatar: proInitials(name) };
}

/**
 * The agent's real Clerk headshot, or null. Clerk keeps a non-empty
 * default-avatar `imageUrl` after a removal, so this returns a URL only when
 * `hasImage` is true — the default avatar is never shown as a "photo".
 */
export function readClerkImageUrl(): string | null {
  const u = clerkUser();
  if (!u?.hasImage) return null;
  return u.imageUrl?.trim() || null;
}

/** True when Clerk can write the headshot (false in preview / pre-hydration). */
export function hasEditableClerkImage(): boolean {
  return typeof clerkUser()?.setProfileImage === "function";
}

const imageListeners = new Set<() => void>();

/**
 * Subscribe to headshot changes. Fires after `setClerkProfileImage` resolves
 * and on any Clerk state change, so a surface reading `readClerkImageUrl` (the
 * header avatar) re-renders at once instead of on the next navigation. Returns
 * the unsubscribe function.
 */
export function subscribeClerkImage(cb: () => void): () => void {
  imageListeners.add(cb);
  const clerk = (globalThis as { Clerk?: ClerkUserGlobal }).Clerk;
  const unlisten = clerk?.addListener?.(cb);
  return () => {
    imageListeners.delete(cb);
    unlisten?.();
  };
}

/**
 * Set (`File`) or remove (`null`) the agent's headshot through Clerk, which
 * validates, hosts, and serves it — Sill stores nothing. Resolves to the new
 * photo URL (null after a removal) and notifies subscribers. Inert (null)
 * with no Clerk user. A Clerk rejection propagates to the caller.
 */
export async function setClerkProfileImage(file: File | null): Promise<string | null> {
  const u = clerkUser();
  if (!u || typeof u.setProfileImage !== "function") return null;
  await u.setProfileImage({ file });
  await u.reload?.();
  const url = readClerkImageUrl();
  for (const cb of [...imageListeners]) cb();
  return url;
}

/**
 * Where the headshot lives, chosen once at module load: Clerk in the app, a
 * local object URL in IS_PREVIEW (no Clerk user). Callers read, subscribe and
 * set through this and never branch on the mode themselves.
 */
export interface ProfileImageSource {
  read: () => string | null;
  subscribe: (cb: () => void) => () => void;
  /** Set (`File`) or remove (`null`); resolves to the new URL (null after a removal). */
  set: (file: File | null) => Promise<string | null>;
  /** False while the control cannot write (no Clerk user yet). */
  canEdit: () => boolean;
  /** True when `set` writes to the real account (worth reporting), not a local stand-in. */
  persisted: boolean;
}

const clerkImageSource: ProfileImageSource = {
  read: readClerkImageUrl,
  subscribe: subscribeClerkImage,
  set: setClerkProfileImage,
  canEdit: hasEditableClerkImage,
  persisted: true,
};

/**
 * IS_PREVIEW only: a local `blob:` URL standing in for a headshot so the
 * operator can click through the photo control with no Clerk user. It is never
 * sent anywhere.
 */
function createPreviewImageSource(): ProfileImageSource {
  let url: string | null = null;
  const listeners = new Set<() => void>();
  return {
    read: () => url,
    subscribe: (cb) => {
      listeners.add(cb);
      return () => {
        listeners.delete(cb);
      };
    },
    set: async (file) => {
      if (url) URL.revokeObjectURL(url);
      url = file ? URL.createObjectURL(file) : null;
      for (const cb of [...listeners]) cb();
      return url;
    },
    canEdit: () => true,
    persisted: false,
  };
}

export const profileImage: ProfileImageSource = IS_PREVIEW
  ? createPreviewImageSource()
  : clerkImageSource;

/** Human sentence for a Clerk rejection (its API-error envelope, else Error). */
export function clerkImageErrorMessage(err: unknown): string {
  const first = (err as { errors?: Array<{ message?: string; longMessage?: string }> } | null)
    ?.errors?.[0];
  const clerkText = first?.longMessage?.trim() || first?.message?.trim();
  if (clerkText) return clerkText;
  if (err instanceof Error && err.message.trim()) return err.message;
  return "Couldn't update your photo. Try again.";
}

/** Read the editable first/last name parts off the Clerk user (empty in preview). */
export function readClerkNameParts(): ClerkNameParts {
  const u = clerkUser();
  return {
    firstName: u?.firstName?.trim() ?? "",
    lastName: u?.lastName?.trim() ?? "",
  };
}

/**
 * True when an editable Clerk user resource exists — gates the in-place name
 * editor. Preview (no provider) and any pre-hydration render fall back to the
 * read-only name.
 */
export function hasEditableClerkUser(): boolean {
  return clerkUser() !== null;
}

/**
 * Write the agent's name back to Clerk and return the refreshed identity
 * (re-read after the update resolves). Provider-agnostic, mirroring
 * `readClerkIdentity` — it touches `window.Clerk.user.update` directly rather
 * than `useUser()`, so it needs no <ClerkProvider> and is naturally inert in
 * preview (no `Clerk.user` → returns the current, empty identity).
 */
export async function updateClerkName(
  next: ClerkNameParts,
): Promise<ClerkIdentity> {
  const u = clerkUser();
  if (!u || typeof u.update !== "function") {
    return readClerkIdentity();
  }
  await u.update({ firstName: next.firstName, lastName: next.lastName });
  return readClerkIdentity();
}

/**
 * Compose the displayed pro identity from the Clerk identity + the professional
 * profile. The Clerk identity wins for name/email/avatar; the profile supplies
 * org/role. When there is no Clerk identity (preview), it falls back to the
 * fixture profile's name/avatar so the preview still reads correctly.
 */
export function composeProUser(
  identity: ClerkIdentity | null,
  profile: ProProfileVM | null,
  isLoading: boolean,
): ProUserResult {
  const name = identity?.name || profile?.name || "Your account";
  const avatar = identity?.avatar || profile?.avatar || proInitials(name);
  return {
    user: {
      name,
      email: identity?.email,
      org: profile?.brokerage ?? "",
      role: profile?.role ?? "agent",
      avatar,
    },
    isLoading,
  };
}
